import { createSelector } from 'reselect';
import {
  getCompanyReportTimestamp,
  getCompanySEOContent,
  getSummary,
  getAnalysis,
  getFormatting,
  getMappedListings,
} from '@company/redux/selectors';
import { shouldSkipDefer } from '@ducks/ui/selectors/shouldSkipDefer';
import { minBy, maxBy } from 'lodash';

interface Information {
  addressLine1: string;
  addressLine2: string;
  canonicalUrl: string;
  mainHeader: string;
  city: string;
  companyId: string;
  companyName: string;
  companyShortName: string;
  country: string;
  countryISO: string;
  currencyISO: string;
  currencySymbol: string;
  description: string;
  exchangeListing: ExchangeItem[]; // TBC
  exchangeSymbol: string;
  exchangeSymbolFiltered: string;
  industryPrimaryName: string;
  industryTertiaryName: string;
  lastAnalysisUpdateFormattedUTC: string;
  lastAnalysisUpdateISOString: string;
  lastAnnualEarninsUpdateFormattedUTC: string;
  lastEODPriceUpdateFormattedUTC: string;
  lastEarningsUpdateFormattedUTC: string;
  lastEstimatesConfirmationFormattedUTC: string;
  marketCapListing: number;
  marketCapPrimary: number;
  originalMarketCap: number;
  schemaDescription: string;
  sharesOutstanding: number;
  snowflakeOverallRating: number;
  snowflakeSentence: string;
  state: string;
  tickerSymbol: string;
  totalEmployees: number[][]; // TBC
  totalEmployeesMin: number;
  totalEmployeesMax: number;
  uniqueSymbol: string; // eslint-disable-line
  url?: string;
  yearFounded: number;
  zipCode: string;
  skipDefer: boolean;
}

interface ExchangeItem {
  tickerSymbol: string;
  exchangeSymbol: string;
  exchangeName: string;
  primarySecurity: string;
  securityName: string;
  exchangeCountryIso: string;
  currencyIso: string;
  startDate: string;
}

export const getInformationSelector = createSelector(
  [
    getSummary,
    getAnalysis,
    getFormatting,
    getMappedListings,
    getCompanyReportTimestamp,
    getCompanySEOContent,
    shouldSkipDefer,
  ],
  (
    summary,
    analysis,
    formatting,
    mappedListings,
    timestamps,
    companySEO,
    skipDefer
  ): Information => {
    const { legacyChartData, marketCapRawData } = analysis;
    const totalEmployees = legacyChartData.totalEmployees;
    const totalEmployeesMin = minBy(totalEmployees, (n) => n[1]);
    const totalEmployeesMax = maxBy(totalEmployees, (n) => n[1]);
    return {
      url: summary.website,
      city: summary.city,
      mainHeader: summary.mainHeader,
      state: summary.state,
      zipCode: summary.postCode,
      country: summary.country,
      companyId: summary.companyId,
      snowflakeOverallRating: summary.overallRating,
      snowflakeSentence: summary.fundamentals,
      countryISO: summary.countryISO,
      description: summary.description,
      canonicalUrl: companySEO.canonicalUrl,
      companyName: summary.name,
      companyShortName: summary.shortName,
      yearFounded: summary.yearFounded,
      addressLine1: summary.address1,
      addressLine2: summary.address2,
      uniqueSymbol: summary.uniqueSymbol,
      tickerSymbol: summary.tickerSymbol,
      totalEmployees,
      totalEmployeesMin: totalEmployeesMin ? totalEmployeesMin[1] : 0,
      totalEmployeesMax: totalEmployeesMax ? totalEmployeesMax[1] : 0,
      currencySymbol: formatting.primaryTradingItemCurrencySymbol,
      currencyISO: formatting.primaryTradingItemCurrencyISO,
      exchangeSymbol: summary.exchangeSymbol,
      exchangeSymbolFiltered: summary.exchangeSymbolFiltered,
      exchangeListing: mappedListings,
      marketCapListing: marketCapRawData.listing,
      marketCapPrimary: marketCapRawData.primary,
      originalMarketCap: marketCapRawData.originalMarketCap,
      sharesOutstanding: marketCapRawData.sharesOutstanding,
      industryPrimaryName: summary.industryData.primaryName,
      industryTertiaryName: summary.industryData.tertiaryName,
      lastAnalysisUpdateISOString: timestamps.lastAnalysisUpdateISOString,
      lastAnalysisUpdateFormattedUTC: timestamps.lastAnalysisUpdateFormattedUTC,
      lastEODPriceUpdateFormattedUTC: timestamps.lastEODPriceUpdateFormattedUTC,
      lastEstimatesConfirmationFormattedUTC:
        timestamps.lastEstimatesConfirmationFormattedUTC,
      lastEarningsUpdateFormattedUTC: timestamps.lastEarningsUpdateFormattedUTC,
      lastAnnualEarninsUpdateFormattedUTC:
        timestamps.lastAnnualEarninsUpdateFormattedUTC,
      schemaDescription: companySEO.description,
      skipDefer,
    };
  }
);
